import { History, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import {
  useDocumentVersions,
  useRestoreDocumentVersion,
} from '../queries';

interface DocumentVersionsPanelProps {
  documentId: number;
  /** When the parent dialog is closed we don't want to fire the request. */
  enabled?: boolean;
}

/** Inline version history for a Document (Wave 12). Newest first, with a
 *  Restore button per snapshot. The current head isn't listed — it's whatever
 *  the parent renders above the panel. */
export function DocumentVersionsPanel({
  documentId,
  enabled = true,
}: DocumentVersionsPanelProps) {
  const { data, isPending, isError, error } = useDocumentVersions(documentId, enabled);
  const restore = useRestoreDocumentVersion();

  return (
    <section aria-labelledby="versions-heading" className="space-y-2">
      <h3 id="versions-heading" className="flex items-center gap-2 text-sm font-medium">
        <History className="size-4" /> Version history
      </h3>
      {isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load versions.'}
        </p>
      ) : !data || data.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No prior versions yet. Edits and restores create snapshots.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {data.map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between gap-3 p-3 text-sm"
            >
              <div className="min-w-0">
                <p className="font-medium">v{v.version_number}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {new Date(v.created).toLocaleString()}
                  {v.change_note ? ` · ${v.change_note}` : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  restore.mutate({ id: documentId, versionNumber: v.version_number })
                }
                disabled={restore.isPending}
                aria-label={`Restore version ${v.version_number}`}
              >
                <RotateCcw className="size-4" /> Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
